const STORAGE_KEY = "gymrite_tracker_submissions";
const DEMO_FLAG_KEY = "gymrite_tracker_submissions_demo_v3";

export const SUBMISSION_SOURCES = {
  MEMBER: "member",
  PUBLIC: "public",
};

export const SUBMISSION_REVIEW = {
  NONE: "none",
  PENDING: "pending",
  OVERDUE: "overdue",
  REVIEWED: "reviewed",
};

export const SUBMISSION_SOURCE_OPTIONS = [
  { value: "all", label: "All sources" },
  { value: SUBMISSION_SOURCES.MEMBER, label: "GymRite members" },
  { value: SUBMISSION_SOURCES.PUBLIC, label: "Public / BMS user" },
];

export const SUBMISSION_REVIEW_OPTIONS = [
  { value: "all", label: "Any review" },
  { value: SUBMISSION_REVIEW.OVERDUE, label: "Overdue" },
  { value: SUBMISSION_REVIEW.PENDING, label: "Pending" },
  { value: SUBMISSION_REVIEW.REVIEWED, label: "Reviewed" },
  { value: SUBMISSION_REVIEW.NONE, label: "No review" },
];

export const getSubmissionSourceLabel = (value) => {
  if (value === SUBMISSION_SOURCES.MEMBER) return "GymRite member";
  if (value === SUBMISSION_SOURCES.PUBLIC) return "BMS user";
  return value || "—";
};

export const getSubmissionReviewLabel = (value) => {
  if (value === SUBMISSION_REVIEW.PENDING) return "Pending";
  if (value === SUBMISSION_REVIEW.OVERDUE) return "Overdue";
  if (value === SUBMISSION_REVIEW.REVIEWED) return "Reviewed";
  return "No review";
};

export const getInitials = (name = "") => {
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const nowIso = () => new Date().toISOString();

const daysAgo = (days, hours = 10) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(hours, 15 + (days % 20), 0, 0);
  return d.toISOString();
};

/** Demo people — GymRite members + public / BMS users. */
const DEMO_PEOPLE = [
  {
    id: "member_aoife_byrne",
    firstName: "Aoife",
    lastName: "Byrne",
    email: "aoife.byrne@email.ie",
    phone: "+353 87 111 2201",
    source: SUBMISSION_SOURCES.MEMBER,
  },
  {
    id: "member_conor_kelly",
    firstName: "Conor",
    lastName: "Kelly",
    email: "conor.kelly@email.ie",
    phone: "+353 86 222 3302",
    source: SUBMISSION_SOURCES.MEMBER,
  },
  {
    id: "member_niamh_walsh",
    firstName: "Niamh",
    lastName: "Walsh",
    email: "niamh.walsh@email.ie",
    phone: "+353 85 333 4403",
    source: SUBMISSION_SOURCES.MEMBER,
  },
  {
    id: "member_sean_murphy",
    firstName: "Seán",
    lastName: "Murphy",
    email: "sean.murphy@email.ie",
    phone: "+353 83 444 5504",
    source: SUBMISSION_SOURCES.MEMBER,
  },
  {
    id: "member_ciara_obrien",
    firstName: "Ciara",
    lastName: "O'Brien",
    email: "ciara.obrien@email.ie",
    phone: "+353 89 555 6605",
    source: SUBMISSION_SOURCES.MEMBER,
  },
  {
    id: "member_liam_doyle",
    firstName: "Liam",
    lastName: "Doyle",
    email: "liam.doyle@email.ie",
    phone: "+353 87 121 3344",
    source: SUBMISSION_SOURCES.MEMBER,
  },
  {
    id: "public_emma_ryan",
    firstName: "Emma",
    lastName: "Ryan",
    email: "emma.ryan@gmail.com",
    phone: "+353 87 666 7706",
    source: SUBMISSION_SOURCES.PUBLIC,
  },
  {
    id: "public_jack_dunne",
    firstName: "Jack",
    lastName: "Dunne",
    email: "jack.dunne@outlook.com",
    phone: "+353 86 777 8807",
    source: SUBMISSION_SOURCES.PUBLIC,
  },
  {
    id: "public_sarah_foley",
    firstName: "Sarah",
    lastName: "Foley",
    email: "sarah.foley@yahoo.com",
    phone: "+353 85 888 9908",
    source: SUBMISSION_SOURCES.PUBLIC,
  },
  {
    id: "public_priya_sharma",
    firstName: "Priya",
    lastName: "Sharma",
    email: "priya.sharma@gmail.com",
    phone: "+353 83 999 1122",
    source: SUBMISSION_SOURCES.PUBLIC,
  },
];

const personName = (person) =>
  `${person.firstName || ""} ${person.lastName || ""}`.trim() || "Unknown";

export const normalizeSubmission = (raw = {}) => {
  if (!raw || typeof raw !== "object") return null;
  const source =
    raw.source === SUBMISSION_SOURCES.PUBLIC
      ? SUBMISSION_SOURCES.PUBLIC
      : SUBMISSION_SOURCES.MEMBER;
  const firstName = raw.firstName || raw.memberFirstName || "";
  const lastName = raw.lastName || raw.memberLastName || "";
  const memberName =
    raw.memberName ||
    `${firstName} ${lastName}`.trim() ||
    raw.email ||
    "Unknown";

  return {
    id:
      raw.id ||
      `sub_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    trackerId: raw.trackerId || "",
    trackerName: raw.trackerName || "",
    formId: raw.formId || "",
    formName: raw.formName || "Form",
    source,
    memberId:
      raw.memberId ||
      (source === SUBMISSION_SOURCES.MEMBER ? raw.id : null),
    memberName,
    firstName,
    lastName,
    email: raw.email || "",
    phone: raw.phone || "",
    statusName: raw.statusName || "Submitted",
    statusColor: raw.statusColor || "#0ea5e9",
    reviewStatus: [
      SUBMISSION_REVIEW.PENDING,
      SUBMISSION_REVIEW.OVERDUE,
      SUBMISSION_REVIEW.REVIEWED,
      SUBMISSION_REVIEW.NONE,
    ].includes(raw.reviewStatus)
      ? raw.reviewStatus
      : SUBMISSION_REVIEW.NONE,
    reviewDetail: raw.reviewDetail || "",
    isNew: Boolean(raw.isNew),
    coachId: raw.coachId || "",
    coachName: raw.coachName || "",
    answers:
      raw.answers && typeof raw.answers === "object" ? raw.answers : {},
    answerSummary: raw.answerSummary || "",
    submittedBy: raw.submittedBy || source,
    submittedAt: raw.submittedAt || nowIso(),
    updatedAt: raw.updatedAt || raw.submittedAt || nowIso(),
  };
};

const buildDemoSubmissions = () => {
  const rows = [];
  let seq = 0;

  const push = (item) => {
    seq += 1;
    rows.push(
      normalizeSubmission({
        id: `sub_demo_${seq}_${item.formId}_${item.person.id}`,
        trackerId: item.trackerId,
        trackerName: item.trackerName,
        formId: item.formId,
        formName: item.formName,
        source: item.person.source,
        memberId:
          item.person.source === SUBMISSION_SOURCES.MEMBER
            ? item.person.id
            : null,
        firstName: item.person.firstName,
        lastName: item.person.lastName,
        email: item.person.email,
        phone: item.person.phone,
        memberName: personName(item.person),
        statusName: item.statusName,
        statusColor: item.statusColor,
        coachName: item.coachName,
        answers: item.answers,
        answerSummary: item.answerSummary,
        reviewStatus: item.reviewStatus || SUBMISSION_REVIEW.NONE,
        reviewDetail: item.reviewDetail || "",
        isNew: Boolean(item.isNew),
        submittedBy: item.person.source,
        submittedAt: daysAgo(item.days, item.hours),
        updatedAt: daysAgo(item.days, item.hours),
      })
    );
  };

  const P = DEMO_PEOPLE;
  const CB = {
    id: "tracker_chasing_better",
    name: "Chasing Better Journey",
  };
  const QR = {
    id: "tracker_quarterly_review",
    name: "Quarterly Member Review",
  };
  const PO = {
    id: "tracker_programme_onboarding",
    name: "Programme Onboarding Journey",
  };
  const CI = {
    id: "tracker_coach_checkin",
    name: "Coach Check-in Journey",
  };
  const CH = {
    id: "tracker_challenge_summer",
    name: "6-Week Summer Challenge",
  };
  const CS = {
    id: "tracker_consultation",
    name: "Free Consultation Request",
  };

  // ——— Chasing Better Journey (multi-form journey) ———
  push({
    trackerId: CB.id,
    trackerName: CB.name,
    formId: "form_cb_signup",
    formName: "Sign-up & Baseline",
    person: P[0],
    statusName: "In Progress",
    statusColor: "#0ea5e9",
    coachName: "Sarah Murphy",
    days: 45,
    hours: 10,
    answerSummary: "Fat loss · Intermediate · 82 kg",
    answers: {
      "Assigned Coach": "Sarah Murphy",
      "Preferred Gym": "GymRite Tallaght",
      "Primary Goal": "Lose 8 kg and rebuild weekly consistency",
      "Current Fitness Level": "Intermediate",
      "Starting Weight": "82 kg",
      Height: "168 cm",
      "Medical Notes / Questionnaire":
        "No current injuries. Mild knee discomfort on lunges.",
      Programme: "Chasing Better",
    },
    reviewStatus: SUBMISSION_REVIEW.REVIEWED,
    reviewDetail: "Baseline reviewed",
  });
  push({
    trackerId: CB.id,
    trackerName: CB.name,
    formId: "form_cb_30",
    formName: "30 Day Review",
    person: P[0],
    statusName: "In Progress",
    statusColor: "#0ea5e9",
    coachName: "Sarah Murphy",
    days: 14,
    hours: 18,
    answerSummary: "79.4 kg · On track · Energy 7",
    answers: {
      "Assigned Coach": "Sarah Murphy",
      "Preferred Gym": "GymRite Tallaght",
      "Current Weight": "79.4 kg",
      "How are you feeling?": "Stronger in the gym, sleep is better",
      "Goal Progress": "On track",
      "Energy Level": "7",
      "Nutrition Adherence": "Good — 80% of meals on plan",
      "Wins this period": "Hit 3 sessions every week",
      "Member Notes": "Want to add a Saturday class",
    },
    reviewStatus: SUBMISSION_REVIEW.PENDING,
    reviewDetail: "30 Day Review · Due in 2d",
  });
  push({
    trackerId: CB.id,
    trackerName: CB.name,
    formId: "form_cb_60",
    formName: "60 Day Review",
    person: P[0],
    statusName: "Review Due",
    statusColor: "#7c3aed",
    coachName: "Sarah Murphy",
    days: 2,
    hours: 9,
    answerSummary: "76.8 kg · 18 sessions · Next: strength",
    answers: {
      "Assigned Coach": "Sarah Murphy",
      "Preferred Gym": "GymRite Tallaght",
      "Current Weight": "76.8 kg",
      "Body Fat Percentage": "28%",
      "How are you feeling?": "Confident and more consistent",
      "Goal Progress": "Ahead of plan",
      "Sessions Attended": "18",
      "Blockers / Challenges": "One travel week mid-month",
      "Next Focus": "Strength block + protein target",
    },
    reviewStatus: SUBMISSION_REVIEW.OVERDUE,
    reviewDetail: "60 Day Review · Overdue 1d",
  });

  push({
    trackerId: CB.id,
    trackerName: CB.name,
    formId: "form_cb_signup",
    formName: "Sign-up & Baseline",
    person: P[1],
    statusName: "Enrolled",
    statusColor: "#22c55e",
    coachName: "James O'Brien",
    days: 20,
    hours: 11,
    answerSummary: "Muscle gain · Beginner · 74 kg",
    answers: {
      "Assigned Coach": "James O'Brien",
      "Preferred Gym": "GymRite Citywest",
      "Primary Goal": "Build muscle and confidence in the gym",
      "Current Fitness Level": "Beginner",
      "Starting Weight": "74 kg",
      Height: "178 cm",
      "Medical Notes / Questionnaire": "Clear — no medical issues",
      Programme: "Chasing Better",
    },
    reviewStatus: SUBMISSION_REVIEW.REVIEWED,
    reviewDetail: "Baseline reviewed",
  });
  push({
    trackerId: CB.id,
    trackerName: CB.name,
    formId: "form_cb_30",
    formName: "30 Day Review",
    person: P[1],
    statusName: "In Progress",
    statusColor: "#0ea5e9",
    coachName: "James O'Brien",
    days: 3,
    hours: 16,
    answerSummary: "75.2 kg · Feeling good · Energy 8",
    answers: {
      "Assigned Coach": "James O'Brien",
      "Preferred Gym": "GymRite Citywest",
      "Current Weight": "75.2 kg",
      "How are you feeling?": "Learning form, energy is up",
      "Goal Progress": "On track",
      "Energy Level": "8",
      "Nutrition Adherence": "Fair — still snacking late",
      "Wins this period": "First pull-up progression",
      "Member Notes": "",
    },
    reviewStatus: SUBMISSION_REVIEW.PENDING,
    reviewDetail: "30 Day Review · Due in 5d",
  });

  push({
    trackerId: CB.id,
    trackerName: CB.name,
    formId: "form_cb_signup",
    formName: "Sign-up & Baseline",
    person: P[6],
    statusName: "Enrolled",
    statusColor: "#22c55e",
    coachName: "Sarah Murphy",
    days: 6,
    hours: 14,
    isNew: true,
    answerSummary: "Public · Tone up · 68 kg",
    answers: {
      "Assigned Coach": "Sarah Murphy",
      "Preferred Gym": "GymRite Tallaght",
      "Primary Goal": "Tone up for summer and feel confident",
      "Current Fitness Level": "Beginner",
      "Starting Weight": "68 kg",
      Height: "165 cm",
      "Medical Notes / Questionnaire": "Asthma — carries inhaler",
      Programme: "Chasing Better",
    },
  });
  push({
    trackerId: CB.id,
    trackerName: CB.name,
    formId: "form_cb_signup",
    formName: "Sign-up & Baseline",
    person: P[7],
    statusName: "Enrolled",
    statusColor: "#22c55e",
    coachName: "Aoife Walsh",
    days: 11,
    hours: 19,
    answerSummary: "Public · HYROX base · 81 kg",
    answers: {
      "Assigned Coach": "Aoife Walsh",
      "Preferred Gym": "GymRite Sandyford",
      "Primary Goal": "Build HYROX base fitness before race season",
      "Current Fitness Level": "Advanced",
      "Starting Weight": "81 kg",
      Height: "183 cm",
      "Medical Notes / Questionnaire": "Previous ankle sprain — cleared",
      Programme: "Chasing Better",
    },
    reviewStatus: SUBMISSION_REVIEW.PENDING,
    reviewDetail: "Baseline · Due in 1d",
  });

  // ——— Coach Check-in Journey (repeating weekly) ———
  [0, 7, 14, 21].forEach((days, index) => {
    push({
      trackerId: CI.id,
      trackerName: CI.name,
      formId: "form_ci_checkin",
      formName: "Weekly check-in",
      person: P[2],
      statusName: index === 0 ? "Needs attention" : "In Progress",
      statusColor: index === 0 ? "#f59e0b" : "#0ea5e9",
      coachName: "Aoife Walsh",
      days,
      hours: 8 + index,
      answerSummary:
        index === 0
          ? "Energy 4 · Low mood · Call needed"
          : `Energy ${7 - (index % 3)} · Sleep ok · Focus set`,
      answers: {
        "Assigned Coach": "Aoife Walsh",
        "Preferred Gym": "GymRite Tallaght",
        "Energy Level": String(index === 0 ? 4 : 7 - (index % 3)),
        "Sleep Quality": index === 0 ? "Poor" : "Good",
        "How are you feeling?":
          index === 0
            ? "Drained after illness — need a lighter week"
            : "Steady week, training feels manageable",
        "Nutrition Adherence": index === 0 ? "Off plan" : "Good",
        "Blockers / Challenges":
          index === 0 ? "Illness last week" : "Work meetings on Tue/Thu",
        "Next Focus":
          index === 0 ? "Recovery sessions only" : "Hit 3 strength sessions",
        "Sessions Attended": String(index === 0 ? 1 : 3),
      },
      reviewStatus:
        index === 0
          ? SUBMISSION_REVIEW.OVERDUE
          : index === 1
            ? SUBMISSION_REVIEW.PENDING
            : SUBMISSION_REVIEW.REVIEWED,
      reviewDetail:
        index === 0
          ? "Weekly review · Overdue 2d"
          : index === 1
            ? "Weekly review · Due in 1d"
            : "Weekly review done",
    });
  });

  push({
    trackerId: CI.id,
    trackerName: CI.name,
    formId: "form_ci_checkin",
    formName: "Weekly check-in",
    person: P[3],
    statusName: "In Progress",
    statusColor: "#0ea5e9",
    coachName: "James O'Brien",
    days: 1,
    hours: 7,
    answerSummary: "Energy 8 · Sleep good · 4 sessions",
    answers: {
      "Assigned Coach": "James O'Brien",
      "Preferred Gym": "GymRite Citywest",
      "Energy Level": "8",
      "Sleep Quality": "Excellent",
      "How are you feeling?": "Motivated — PBs on deadlift",
      "Nutrition Adherence": "Excellent",
      "Blockers / Challenges": "None",
      "Next Focus": "Add conditioning finisher",
      "Sessions Attended": "4",
    },
    reviewStatus: SUBMISSION_REVIEW.PENDING,
    reviewDetail: "Weekly review · Due in 3d",
  });

  // ——— Programme Onboarding ———
  push({
    trackerId: PO.id,
    trackerName: PO.name,
    formId: "form_po_week1",
    formName: "Week 1 — Onboarding",
    person: P[4],
    statusName: "In Progress",
    statusColor: "#0ea5e9",
    coachName: "Sarah Murphy",
    days: 35,
    hours: 10,
    answerSummary: "HYROX · Intermediate · Week 1",
    answers: {
      "Assigned Coach": "Sarah Murphy",
      "Preferred Gym": "GymRite Sandyford",
      Programme: "HYROX Complete",
      "Current Fitness Level": "Intermediate",
      "Fitness Goal":
        "Complete HYROX Open in under 90 minutes with solid running base",
      "Primary Goal": "Finish HYROX Open confidently",
      "Training Experience":
        "2 years gym training, first HYROX season this year",
      "Medical Notes / Questionnaire": "No current injuries",
      "Starting Weight": "70 kg",
      "Current Week / Stage": "Week 1",
    },
    reviewStatus: SUBMISSION_REVIEW.REVIEWED,
    reviewDetail: "Week 1 reviewed",
  });
  push({
    trackerId: PO.id,
    trackerName: PO.name,
    formId: "form_po_week4",
    formName: "Week 4 — Progress",
    person: P[4],
    statusName: "In Progress",
    statusColor: "#0ea5e9",
    coachName: "Sarah Murphy",
    days: 8,
    hours: 12,
    answerSummary: "69.2 kg · 11 sessions · Energy 7",
    answers: {
      "Assigned Coach": "Sarah Murphy",
      "Preferred Gym": "GymRite Sandyford",
      Programme: "HYROX Complete",
      "Current Weight": "69.2 kg",
      "Sessions Attended": "11",
      "Energy Level": "7",
      "How are you feeling?": "Running economy improving",
      "Goal Progress": "On track",
      "Blockers / Challenges": "One missed sled session",
      "Current Week / Stage": "Week 4",
    },
    reviewStatus: SUBMISSION_REVIEW.PENDING,
    reviewDetail: "Week 4 review · Due in 4d",
  });
  push({
    trackerId: PO.id,
    trackerName: PO.name,
    formId: "form_po_week1",
    formName: "Week 1 — Onboarding",
    person: P[1],
    statusName: "Enrolled",
    statusColor: "#22c55e",
    coachName: "James O'Brien",
    days: 12,
    hours: 15,
    answerSummary: "HYROX · Beginner · Week 1",
    answers: {
      "Assigned Coach": "James O'Brien",
      "Preferred Gym": "GymRite Citywest",
      Programme: "HYROX Complete",
      "Current Fitness Level": "Beginner",
      "Fitness Goal": "Build engine and finish first HYROX without stopping",
      "Primary Goal": "Complete first HYROX event",
      "Training Experience": "Mostly class-based training for 8 months",
      "Medical Notes / Questionnaire": "Clear",
      "Starting Weight": "74 kg",
      "Current Week / Stage": "Week 1",
    },
  });

  // ——— Quarterly Member Review ———
  push({
    trackerId: QR.id,
    trackerName: QR.name,
    formId: "form_qr_start",
    formName: "Quarter Start",
    person: P[2],
    statusName: "In Progress",
    statusColor: "#0ea5e9",
    coachName: "James O'Brien",
    days: 40,
    hours: 11,
    answerSummary: "3 sessions/week · Q goal set",
    answers: {
      "Assigned Coach": "James O'Brien",
      "Preferred Gym": "GymRite Tallaght",
      "Attendance Commitment": "3 sessions / week",
      "Quarterly Goal": "Drop 4 kg and hit 12 unbroken pull-ups",
      "Current Week / Stage": "Q2 start",
      "Member Notes": "Prefer evening classes",
    },
    reviewStatus: SUBMISSION_REVIEW.REVIEWED,
    reviewDetail: "Quarter start locked",
  });
  push({
    trackerId: QR.id,
    trackerName: QR.name,
    formId: "form_qr_checkin",
    formName: "Quarter Review Update",
    person: P[2],
    statusName: "In Progress",
    statusColor: "#0ea5e9",
    coachName: "James O'Brien",
    days: 5,
    hours: 17,
    answerSummary: "Attendance honoured · Goal progress good",
    answers: {
      "Assigned Coach": "James O'Brien",
      "Preferred Gym": "GymRite Tallaght",
      "Attendance Honoured": "Yes",
      "Goal Achieved": "Partially",
      "Goal Progress": "Good — 2 kg down, pull-ups at 8",
      Milestone: "First unassisted pull-up set",
      "Attendance Percentage": "86%",
      "Wins this period": "Consistency through holidays",
      "Blockers / Challenges": "Two sick days",
      "Next Focus": "Strength endurance",
    },
    reviewStatus: SUBMISSION_REVIEW.PENDING,
    reviewDetail: "Q review · Due in 6d",
  });
  push({
    trackerId: QR.id,
    trackerName: QR.name,
    formId: "form_qr_start",
    formName: "Quarter Start",
    person: P[5],
    statusName: "Enrolled",
    statusColor: "#22c55e",
    coachName: "James O'Brien",
    days: 9,
    hours: 13,
    answerSummary: "4 sessions/week · Race prep goal",
    answers: {
      "Assigned Coach": "James O'Brien",
      "Preferred Gym": "GymRite Citywest",
      "Attendance Commitment": "4 sessions / week",
      "Quarterly Goal": "Run a sub-25 min 5k and hold gym days",
      "Current Week / Stage": "Q2 start",
      "Member Notes": "",
    },
  });

  // ——— 6-Week Summer Challenge ———
  push({
    trackerId: CH.id,
    trackerName: CH.name,
    formId: "form_ch_entry",
    formName: "Challenge entry",
    person: P[0],
    statusName: "In Progress",
    statusColor: "#0ea5e9",
    coachName: "Sarah Murphy",
    days: 28,
    hours: 10,
    answerSummary: "Entry · Score 32 · 82 kg",
    answers: {
      "Assigned Coach": "Sarah Murphy",
      "Preferred Gym": "GymRite Tallaght",
      "Primary Goal": "Beat my push-up score and stay consistent 6 weeks",
      "Current Fitness Level": "Intermediate",
      "Starting Weight": "82 kg",
      "Challenge Score": "32",
      "Medical Notes / Questionnaire": "Knees OK with warm-up",
    },
    reviewStatus: SUBMISSION_REVIEW.REVIEWED,
    reviewDetail: "Entry verified",
  });
  push({
    trackerId: CH.id,
    trackerName: CH.name,
    formId: "form_ch_mid",
    formName: "Week 3 check-in",
    person: P[0],
    statusName: "In Progress",
    statusColor: "#0ea5e9",
    coachName: "Sarah Murphy",
    days: 10,
    hours: 19,
    answerSummary: "Mid · Score 41 · 80.1 kg",
    answers: {
      "Assigned Coach": "Sarah Murphy",
      "Preferred Gym": "GymRite Tallaght",
      "Current Weight": "80.1 kg",
      "Challenge Score": "41",
      "How are you feeling?": "Stronger upper body, less sore",
      "Blockers / Challenges": "None",
    },
    reviewStatus: SUBMISSION_REVIEW.PENDING,
    reviewDetail: "Mid check-in · Due in 2d",
  });
  push({
    trackerId: CH.id,
    trackerName: CH.name,
    formId: "form_ch_final",
    formName: "Final result",
    person: P[0],
    statusName: "Journey Completed",
    statusColor: "#16a34a",
    coachName: "Sarah Murphy",
    days: 1,
    hours: 20,
    answerSummary: "Final · Score 48 · Goal achieved",
    answers: {
      "Assigned Coach": "Sarah Murphy",
      "Preferred Gym": "GymRite Tallaght",
      "Challenge Score": "48",
      "Current Weight": "78.6 kg",
      "Challenge Video": "Uploaded — final_attempt.mp4",
      "Total Points": "480",
      "Goal Achieved": "Yes",
      "Member Notes": "Want the autumn challenge next",
    },
    reviewStatus: SUBMISSION_REVIEW.PENDING,
    reviewDetail: "Final verification · Due today",
  });
  push({
    trackerId: CH.id,
    trackerName: CH.name,
    formId: "form_ch_entry",
    formName: "Challenge entry",
    person: P[8],
    statusName: "Enrolled",
    statusColor: "#22c55e",
    coachName: "Aoife Walsh",
    days: 4,
    hours: 15,
    isNew: true,
    answerSummary: "Public entry · Score 28",
    answers: {
      "Assigned Coach": "Aoife Walsh",
      "Preferred Gym": "GymRite Sandyford",
      "Primary Goal": "Hit 50 push-ups by week 6",
      "Current Fitness Level": "Beginner",
      "Starting Weight": "64 kg",
      "Challenge Score": "28",
      "Medical Notes / Questionnaire": "Clear",
    },
  });
  push({
    trackerId: CH.id,
    trackerName: CH.name,
    formId: "form_ch_entry",
    formName: "Challenge entry",
    person: P[7],
    statusName: "Enrolled",
    statusColor: "#22c55e",
    coachName: "Aoife Walsh",
    days: 15,
    hours: 12,
    answerSummary: "Public entry · Score 45",
    answers: {
      "Assigned Coach": "Aoife Walsh",
      "Preferred Gym": "GymRite Sandyford",
      "Primary Goal": "Stay top 10 on the leaderboard",
      "Current Fitness Level": "Advanced",
      "Starting Weight": "81 kg",
      "Challenge Score": "45",
      "Medical Notes / Questionnaire": "Cleared ankle",
    },
    reviewStatus: SUBMISSION_REVIEW.REVIEWED,
    reviewDetail: "Entry verified",
  });

  // ——— Free Consultation Request ———
  push({
    trackerId: CS.id,
    trackerName: CS.name,
    formId: "form_cs_request",
    formName: "Consultation request",
    person: P[9],
    statusName: "Requested",
    statusColor: "#0ea5e9",
    coachName: "James O'Brien",
    days: 0,
    hours: 15,
    isNew: true,
    answerSummary: "Public lead · Fat loss consult",
    answers: {
      "Assigned Coach": "James O'Brien",
      "Preferred Gym": "GymRite Tallaght",
      "Reason for consultation": "Want structured fat-loss coaching with accountability",
      "Primary Goal": "Lose 10 kg before Christmas",
      "Current Fitness Level": "Beginner",
      "Preferred times": "Tue/Thu evenings after 18:00",
      Programme: "Chasing Better",
      "Medical Notes / Questionnaire": "No issues",
    },
    reviewStatus: SUBMISSION_REVIEW.PENDING,
    reviewDetail: "Coach follow-up · Due in 3d",
  });
  push({
    trackerId: CS.id,
    trackerName: CS.name,
    formId: "form_cs_request",
    formName: "Consultation request",
    person: P[3],
    statusName: "Booked",
    statusColor: "#8b5cf6",
    coachName: "Sarah Murphy",
    days: 7,
    hours: 13,
    answerSummary: "Member · Online coaching interest",
    answers: {
      "Assigned Coach": "Sarah Murphy",
      "Preferred Gym": "GymRite Citywest",
      "Reason for consultation": "Considering online coaching add-on",
      "Primary Goal": "Stay accountable while travelling for work",
      "Current Fitness Level": "Intermediate",
      "Preferred times": "Lunchtime video calls",
      Programme: "",
      "Medical Notes / Questionnaire": "",
    },
    reviewStatus: SUBMISSION_REVIEW.REVIEWED,
    reviewDetail: "Call booked",
  });
  push({
    trackerId: CS.id,
    trackerName: CS.name,
    formId: "form_cs_outcome",
    formName: "Consultation outcome",
    person: P[3],
    statusName: "Joined programme",
    statusColor: "#16a34a",
    coachName: "Sarah Murphy",
    days: 4,
    hours: 16,
    answerSummary: "Joined · Next: onboarding",
    answers: {
      "Assigned Coach": "Sarah Murphy",
      "Preferred Gym": "GymRite Citywest",
      "How are you feeling?": "Excited to start",
      "Next Focus": "Start Programme Onboarding Week 1",
      "Member Notes": "Prefers WhatsApp reminders",
      "Follow-up Date": "2026-09-20",
    },
    reviewStatus: SUBMISSION_REVIEW.REVIEWED,
    reviewDetail: "Joined programme",
  });
  push({
    trackerId: CS.id,
    trackerName: CS.name,
    formId: "form_cs_request",
    formName: "Consultation request",
    person: P[6],
    statusName: "Requested",
    statusColor: "#0ea5e9",
    coachName: "James O'Brien",
    days: 2,
    hours: 11,
    answerSummary: "Public · Strength consult",
    answers: {
      "Assigned Coach": "James O'Brien",
      "Preferred Gym": "GymRite Tallaght",
      "Reason for consultation": "Want a strength-focused programme review",
      "Primary Goal": "Add 10 kg to squat in 12 weeks",
      "Current Fitness Level": "Advanced",
      "Preferred times": "Saturday mornings",
      Programme: "HYROX Complete",
      "Medical Notes / Questionnaire": "Old shoulder strain — managed",
    },
  });

  return rows.sort(
    (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)
  );
};

export const hasSubmissionDemoSeed = () =>
  Boolean(localStorage.getItem(DEMO_FLAG_KEY));

export const seedDemoSubmissions = ({ force = false } = {}) => {
  const existing = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  if (!force && existing.length > 0 && hasSubmissionDemoSeed()) {
    return existing.map((item) => normalizeSubmission(item)).filter(Boolean);
  }

  const seeded = buildDemoSubmissions();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
  localStorage.setItem(DEMO_FLAG_KEY, nowIso());
  return seeded;
};

export const getSubmissions = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(parsed) ? parsed : [];
    if (list.length === 0 || !hasSubmissionDemoSeed()) {
      return seedDemoSubmissions({ force: true });
    }
    return list.map((item) => normalizeSubmission(item)).filter(Boolean);
  } catch {
    return seedDemoSubmissions({ force: true });
  }
};

export const getSubmissionById = (id) =>
  getSubmissions().find((item) => item.id === id) || null;

export const getSubmissionsForTracker = (trackerId) =>
  getSubmissions().filter((item) => item.trackerId === trackerId);

export const saveSubmission = (submission) => {
  const shaped = normalizeSubmission({
    ...submission,
    updatedAt: nowIso(),
  });
  if (!shaped) return null;
  const list = getSubmissions();
  const index = list.findIndex((item) => item.id === shaped.id);
  if (index >= 0) list[index] = shaped;
  else list.unshift(shaped);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  return shaped;
};

export const deleteSubmission = (id) => {
  const next = getSubmissions().filter((item) => item.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
};

export const countSubmissionsForTracker = (trackerId) =>
  getSubmissionsForTracker(trackerId).length;

/** Stable key for unique people across submissions. */
export const getSubmissionPersonKey = (row = {}) =>
  row.memberId || row.email || row.memberName || row.id || "";

export const countPeopleForTracker = (trackerId) => {
  const keys = new Set();
  getSubmissionsForTracker(trackerId).forEach((row) => {
    const key = getSubmissionPersonKey(row);
    if (key) keys.add(key);
  });
  return keys.size;
};

export const countUniquePeople = (rows = getSubmissions()) => {
  const keys = new Set();
  (rows || []).forEach((row) => {
    const key = getSubmissionPersonKey(row);
    if (key) keys.add(key);
  });
  return keys.size;
};

export const countReviewsDue = (rows = getSubmissions()) =>
  (rows || []).filter(
    (row) =>
      row.reviewStatus === SUBMISSION_REVIEW.PENDING ||
      row.reviewStatus === SUBMISSION_REVIEW.OVERDUE
  ).length;

/** Force-refresh demo submissions (dev helper). */
export const reloadDemoSubmissions = () =>
  seedDemoSubmissions({ force: true });
